import { appRoutes } from '@/constants'
import { Lock, Shield, Sparkles, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { HeroBackground } from '@/components/ui/HeroBackground'
import { motion, Variants } from 'framer-motion'

export const HeroSection = () => {
  const navigate = useNavigate()

  const doomScrollingVariants: Variants = {
    hidden: {
      y: 100,
      opacity: 0,
      scale: 0.3,
      filter: 'blur(20px)'
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        duration: 1.2,
        ease: [0.43, 0.13, 0.23, 0.96] as [number, number, number, number],
        opacity: { duration: 0.8 },
        scale: {
          duration: 1.2,
          ease: 'easeOut'
        }
      }
    }
  }

  const headingVariants: Variants = {
    hidden: {
      y: 50,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }

  const subtitleVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 1.4,
        ease: 'easeOut'
      }
    }
  }

  const badgeContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delay: 1.8,
        staggerChildren: 0.15
      }
    }
  }

  const badgeVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0,
      rotate: -180
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        damping: 15,
        stiffness: 100
      }
    }
  }

  const buttonVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      y: 30
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        delay: 2.3,
        duration: 0.6,
        type: 'spring'
      }
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 0 10px rgba(244, 200, 106, 0.6)',
      transition: {
        delay: 0,
        duration: 0.3
      }
    },
    tap: {
      scale: 0.95,
      transition: {
        delay: 0,
        duration: 0.1
      }
    }
  }

  const buttonInteraction: Variants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 0 10px rgba(244, 200, 106, 0.6)',
      transition: { duration: 0.3, delay: 0 }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1, delay: 0 }
    }
  }

  const pulseVariants: Variants = {
    visible: {
      boxShadow: [
        '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        '0 0 30px rgba(244, 200, 106, 0.4)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 3
      }
    }
  }

  return (
    <section className="relative overflow-hidden px-6 py-24 pt-48 text-center">
      <HeroBackground opacity={0.2} />
      <div className="relative z-10 mx-auto max-w-5xl">
        <motion.h1
          className="font-heading mb-6 text-6xl leading-tight md:text-7xl"
          initial="hidden"
          animate="visible"
          variants={headingVariants}
        >
          Break Free From
          <span className="mt-2 block">
            <motion.span
              className="inline-block"
              initial="hidden"
              animate="visible"
              variants={doomScrollingVariants}
              style={{
                background:
                  'linear-gradient(to right, #dc2626, #ff4800ff, #ff7b00ff, #ff4800ff, #dc2626)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              <span className="inline-block text-7xl font-black md:text-8xl">
                Doom Scrolling
              </span>
            </motion.span>
          </span>
        </motion.h1>

        <motion.p
          className="text-shire-bark mb-8 leading-relaxed sm:text-xl md:text-2xl"
          initial="hidden"
          animate="visible"
          variants={subtitleVariants}
        >
          The only app blocker that can't be bypassed.
          <br />
          <span className="font-semibold">
            Lock your accounts behind real-world challenges.
          </span>
        </motion.p>

        <motion.div
          className="mb-8 flex flex-wrap justify-center gap-4 text-sm"
          initial="hidden"
          animate="visible"
          variants={badgeContainerVariants}
        >
          <motion.span
            className="flex items-center gap-1 rounded-full bg-white/40 px-3 py-1"
            variants={badgeVariants}
            whileHover={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
          >
            <Sparkles className="size-4" />
            Completely Free
          </motion.span>
          <motion.span
            className="flex items-center gap-1 rounded-full bg-white/40 px-3 py-1"
            variants={badgeVariants}
            whileHover={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
          >
            <Shield className="size-4" />
            Actually Effective
          </motion.span>
          <motion.span
            className="flex items-center gap-1 rounded-full bg-white/40 px-3 py-1"
            variants={badgeVariants}
            whileHover={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
          >
            <Zap className="size-4" />
            Setup Takes 2 Minutes
          </motion.span>
        </motion.div>

        <motion.button
          className="bg-shire-sun font-ui text-shire-dark hover:bg-shire-bark hover:text-shire-light relative inline-flex items-center gap-2 rounded-full px-8 py-4 text-xl font-semibold shadow-lg transition-colors duration-300"
          onClick={() => navigate(appRoutes.autoLockPlanner)}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          whileTap="tap"
          variants={{ ...buttonVariants, ...buttonInteraction }}
          transition={{
            duration: 0.3
          }}
        >
          <motion.div
            animate="visible"
            variants={pulseVariants}
            className="absolute inset-0 rounded-full"
            style={{ zIndex: -1 }}
          />
          <Lock className="size-5" />
          Lock My First Account
        </motion.button>
      </div>
    </section>
  )
}
